"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.relationMissingRule = void 0;
exports.getMissingRelations = getMissingRelations;
const utils_1 = require("@azimutt/utils");
const database_1 = require("../../database");
const databaseUtils_1 = require("../../databaseUtils");
const rule_1 = require("../rule");
/**
 * If relations are not defined as foreign key, it could be great to identify them
 */
const ruleId = 'relation-missing';
const ruleName = 'missing relation';
const ruleDescription = 'relations not present in the database but could exist';
const CustomRuleConf = rule_1.RuleConf.extend({
    ignores: database_1.RelationId.array().optional(),
}).strict().describe('RelationMissingConf');
exports.relationMissingRule = {
    id: ruleId,
    name: ruleName,
    description: ruleDescription,
    conf: { level: rule_1.RuleLevel.enum.medium },
    zConf: CustomRuleConf,
    analyze(conf, now, db, queries, history, reference) {
        const refIgnores = reference.map(r => r.extra?.relation ? (0, databaseUtils_1.relationToRef)(r.extra?.relation) : undefined).filter(utils_1.isNotUndefined);
        const ignores = refIgnores.concat(conf.ignores?.map(databaseUtils_1.relationRefFromId) || []);
        return getMissingRelations(db.entities || [], db.relations || [])
            .filter(r => !ignores.some(i => (0, databaseUtils_1.relationRefSame)(i, (0, databaseUtils_1.relationToRef)(r))))
            .map(r => ({
            ruleId,
            ruleName,
            ruleLevel: conf.level,
            message: `Create a relation from ${(0, databaseUtils_1.attributesRefToId)({ ...r.src, attributes: r.src.attrs })} to ${(0, databaseUtils_1.attributesRefToId)({ ...r.ref, attributes: r.ref.attrs })}.`,
            entity: (0, databaseUtils_1.relationLinkToEntityRef)(r.src),
            attribute: r.src.attrs[0],
            extra: { relation: r }
        }));
    }
};
// same as frontend/src/PagesComponents/Organization_/Project_/Views/Modals/SchemaAnalysis/RelationMissing.elm
function getMissingRelations(entities, relations) {
    const tableEntities = entities.filter(e => e.kind === undefined || e.kind === 'table'); // don't suggest relation to a view
    const entitiesByName = (0, utils_1.groupBy)(tableEntities, e => (0, utils_1.splitWords)(e.name).map(utils_1.singular).join('_'));
    const entitiesByPrefixName = (0, utils_1.groupBy)(tableEntities.filter(e => (0, utils_1.splitWords)(e.name).length > 1), e => (0, utils_1.splitWords)(e.name).slice(1).map(utils_1.singular).join('_'));
    const entitiesByPrefixName2 = (0, utils_1.groupBy)(tableEntities.filter(e => (0, utils_1.splitWords)(e.name).length > 2), e => (0, utils_1.splitWords)(e.name).slice(2).map(utils_1.singular).join('_'));
    const relationsBySrc = (0, utils_1.groupBy)(relations, r => (0, databaseUtils_1.entityRefToId)(r.src));
    return tableEntities.flatMap(entity => {
        return (0, databaseUtils_1.flattenAttributes)(entity.attrs).flatMap(({ path, ...attr }) => {
            const attrWords = (0, utils_1.splitWords)(attr.name).map(utils_1.singular);
            const lastWord = attrWords[attrWords.length - 1];
            let results = [];
            if (lastWord === 'id' && attrWords.length > 1) {
                return guessAllRelationTargetsWithPoly(entitiesByName, entitiesByPrefixName, entitiesByPrefixName2, entity, path, attrWords);
            }
            else if (lastWord.endsWith('id') && lastWord.length > 2) {
                const words = [...attrWords.slice(0, -1), lastWord.slice(0, -2), lastWord.slice(-2)];
                return guessAllRelationTargetsWithPoly(entitiesByName, entitiesByPrefixName, entitiesByPrefixName2, entity, path, words);
            }
            else if (lastWord === 'by') {
                const entityRef = (0, databaseUtils_1.entityToRef)(entity);
                let targets = guessRelationTargets(entitiesByName, entityRef, ['user', 'id']);
                targets = targets.length === 0 ? guessRelationTargets(entitiesByName, entityRef, ['account', 'id']) : targets;
                results = targets.map(ref => ({ src: { ...entityRef, attrs: [path] }, ref: { ...ref.entity, attrs: [ref.attr] }, origin: 'infer-name' }));
            }
            return results;
        });
    }).filter(r => !loopingRelation(r)).filter(r => !relationAlreadyExist(relationsBySrc, r));
}
function guessAllRelationTargetsWithPoly(entitiesByName, entitiesByPrefixName, entitiesByPrefixName2, entity, attrPath, attrWords) {
    const poly = getPolymorphicColumn(entity, attrPath);
    const entityRef = (0, databaseUtils_1.entityToRef)(entity);
    if (poly) {
        return poly.values.flatMap(value => {
            const words = (0, utils_1.splitWords)((0, databaseUtils_1.attributeValueToString)(value)).map(utils_1.singular).concat(['id']);
            return guessRelationTargetsWithPrefixes(entitiesByName, entitiesByPrefixName, entitiesByPrefixName2, entityRef, attrPath, words)
                .map(r => ({ ...r, polymorphic: { attribute: poly.path, value } }));
        });
    }
    else {
        return guessRelationTargetsWithPrefixes(entitiesByName, entitiesByPrefixName, entitiesByPrefixName2, entityRef, attrPath, attrWords);
    }
}
function guessRelationTargetsWithPrefixes(entitiesByName, entitiesByPrefixName, entitiesByPrefixName2, entityRef, attrPath, attrWords) {
    let targets = guessRelationTargets(entitiesByName, entityRef, attrWords);
    targets = targets.length === 0 ? guessRelationTargets(entitiesByPrefixName, entityRef, attrWords) : targets;
    targets = targets.length === 0 ? guessRelationTargets(entitiesByPrefixName2, entityRef, attrWords) : targets;
    return targets.map(ref => ({ src: { ...entityRef, attrs: [attrPath] }, ref: { ...ref.entity, attrs: [ref.attr] }, origin: 'infer-name' }));
}
function guessRelationTargets(entitiesByName, ref, attrWords) {
    return (entitiesByName[attrWords.slice(0, -1).join('_')] || []).concat(entitiesByName[attrWords.slice(1, -1).join('_')] || [], // remove hypothetical prefixes (up to 3)
    entitiesByName[attrWords.slice(2, -1).join('_')] || [], entitiesByName[attrWords.slice(3, -1).join('_')] || [], entitiesByName[attrWords.slice(0, -2).join('_')] || [], // remove hypothetical suffixes (up to 3)
    entitiesByName[attrWords.slice(0, -3).join('_')] || [], entitiesByName[attrWords.slice(0, -4).join('_')] || [], entitiesByName[attrWords.slice(1, -2).join('_')] || [], // remove hypothetical prefixes & suffixes (up to 2)
    entitiesByName[attrWords.slice(2, -2).join('_')] || [], entitiesByName[attrWords.slice(1, -3).join('_')] || [], entitiesByName[attrWords.slice(2, -3).join('_')] || []).sort((a, b) => namespaceScore(a, ref) - namespaceScore(b, ref)).flatMap((entity) => {
        const attrName = attrWords.slice(-1)[0];
        const attr = entity.attrs?.find(a => a.name === attrName) || entity.attrs?.find(a => {
            const words = a.name.endsWith('id') ? (0, utils_1.splitWords)(a.name.slice(0, -2)).concat(['id']) : (0, utils_1.splitWords)(a.name);
            return words.join('_') === attrWords.slice(words.length * -1).join('_');
        });
        return attr ? [{ entity: (0, databaseUtils_1.entityToRef)(entity), attr: [attr.name] }] : [];
    });
}
// lower is nearer
const namespaceScore = (e, ref) => (e.schema !== ref.schema ? 1 : 0) + (e.catalog !== ref.catalog ? 10 : 0) + (e.database !== ref.database ? 100 : 0);
function getPolymorphicColumn(entity, attribute) {
    const suffixes = ['type', 'kind', 'class'];
    const parent = attribute.slice(0, -1);
    const last = attribute[attribute.length - 1];
    const prefix = last.endsWith('ids') ? last.slice(0, -3) : (last.endsWith('id') ? last.slice(0, -2) : last);
    const attr = (0, databaseUtils_1.getPeerAttributes)(entity.attrs, parent).find(a => suffixes.some(s => a.name.toLowerCase() === `${prefix}${s}`.toLowerCase()));
    return attr?.stats?.distinctValues ? { path: parent.concat([attr.name]), values: attr.stats.distinctValues } : undefined;
}
function loopingRelation(r) {
    return (0, databaseUtils_1.entityRefToId)(r.src) === (0, databaseUtils_1.entityRefToId)(r.ref) && r.src.attrs.map(databaseUtils_1.attributePathToId).join(',') === r.ref.attrs.map(databaseUtils_1.attributePathToId).join(',');
}
function relationAlreadyExist(relationsBySrc, relation) {
    const relationRefEntity = (0, databaseUtils_1.entityRefToId)(relation.ref);
    const relationSrcAttrs = relation.src.attrs.map(databaseUtils_1.attributePathToId).join(',');
    const relationRefAttrs = relation.ref.attrs.map(databaseUtils_1.attributePathToId).join(',');
    const sameRelations = (relationsBySrc[(0, databaseUtils_1.entityRefToId)(relation.src)] || []) // from same entity
        .filter(r => (0, databaseUtils_1.entityRefToId)(r.ref) === relationRefEntity) // to same entity
        .filter(r => r.src.attrs.map(databaseUtils_1.attributePathToId).join(',') === relationSrcAttrs) // from same attributes
        .filter(r => r.ref.attrs.map(databaseUtils_1.attributePathToId).join(',') === relationRefAttrs); // to same attributes
    return sameRelations.length > 0;
}
//# sourceMappingURL=relationMissing.js.map